import React, { useState } from 'react';
import { Stack } from '@mui/material';
import StyledModal from '../../../Components/GenericModal/StyledModal';
import { DebouncedFormTextField } from '../../../Components/CustomUIElements/FormTextField';
import ModalFooterButtons from '../../../Components/GenericModal/ModalFooterButtons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Api from '../../API';
import { ISnackbarInput } from '../../../Components/CustomUIElements/StyledSnackbar';

export default function NewJobTemplateModal({
  isOpen,
  handleClose,
  jobId,
  setSnackbar,
  selectedTemplateName
}: {
  isOpen: boolean;
  handleClose: () => void;
  jobId?: number; // If jobId is provided, the modal will save the job as a template. If not, the modal will create a new job template.
  setSnackbar: (snackbar: ISnackbarInput) => void;
  selectedTemplateName?: string; // If selectedTemplateName is provided, the modal is used to clone a template.
}) {
  const [templateName, setTemplateName] = useState<string>(
    selectedTemplateName ? `${selectedTemplateName} - copy` : ''
  );
  const [error, setError] = useState<string>('');

  const queryClient = useQueryClient();

  const { mutate: createJobTemplate, isLoading: creatingJobTemplate } = useMutation(
    async () => {
      const { res } = await Api.createJobTemplate({ job: { job_template_name: templateName } });
      return res;
    },
    {
      onSuccess: (res: { url: string }) => {
        window.location.href = res.url;
        handleClose();
      },
      onError: (error: { errors: string }) => {
        setSnackbar({ message: error.errors, state: 'error' });
      }
    }
  );

  const { mutate: saveOrCloneTemplate, isLoading: savingOrCloningTemplate } = useMutation(
    async (id: number) => {
      const params = {
        id,
        job: { job_template_name: templateName }
      };
      const { res } = selectedTemplateName
        ? await Api.cloneJobTemplate(params)
        : await Api.saveJobAsTemplate(params);
      return res;
    },
    {
      onSuccess: (res: { message: string }) => {
        setSnackbar({ message: res.message, state: 'success' });
        queryClient.invalidateQueries({ queryKey: ['job templates'] });
        handleClose();
      },
      onError: (error: { errors: string }) => {
        setSnackbar({ message: error.errors, state: 'error' });
      }
    }
  );

  const { data: validateJobTemplateNameData } = useQuery({
    queryKey: [templateName],
    queryFn: async () => {
      const { res } = await Api.validateJobTemplateName({ job_template_name: templateName });
      return res;
    }
  });

  const handleCreateJobTemplate = () => {
    if (!templateName.trim()) {
      setError('Template name is required');
    } else if (validateJobTemplateNameData?.error) {
      setError(validateJobTemplateNameData.error);
    } else {
      jobId ? saveOrCloneTemplate(jobId) : createJobTemplate();
    }
  };

  return (
    <StyledModal
      isOpen={isOpen}
      label="new-job-template"
      handleClose={handleClose}
      styleOverrides={{
        height: '300px',
        width: '600px',
        padding: '40px'
      }}
    >
      <Stack
        sx={{
          width: '100%',
          height: '100%',
          rowGap: 3
        }}
      >
        <Stack
          sx={{
            fontWeight: 'bold',
            color: '#084D6D',
            fontSize: '25px'
          }}
        >
          {`${selectedTemplateName ? 'Clone' : 'New'} Job Template`}
        </Stack>
        <Stack sx={{ flexGrow: 1 }}>
          <DebouncedFormTextField
            label="Template name"
            defaultValue={templateName}
            onDebouncedChange={(value: string | number) => setTemplateName(String(value))}
            debounceTime={300}
            required
            styles={{ width: '65%' }}
            error={error}
          />
        </Stack>
        <ModalFooterButtons
          primaryButtonText={selectedTemplateName ? 'Confirm' : 'Create'}
          primaryButtonCallback={handleCreateJobTemplate}
          isLoading={savingOrCloningTemplate || creatingJobTemplate}
          secondaryButtonText="Cancel"
          secondaryButtonCallback={handleClose}
          primaryButtonMinWidth="95px"
        />
      </Stack>
    </StyledModal>
  );
}
